import type { DbTableResult } from './dbTableAnswers'

type SpendAnswer =
  | { kind: 'text'; content: string }
  | { kind: 'table'; content: string; table: DbTableResult }
import {
  fetchSpendCategory,
  fetchSpendDashboard,
  fetchSpendSupplier,
  formatRatio,
  formatSpend,
  formatYoy,
} from './spendCubeApi'

const NO_COMPANY =
  'Spend answers need one company. View as that company, or sign in as a member of it.'
const NO_CUBE = 'No spend file for this company yet.'

function spendHref(parentName: string, linkSpend: boolean): string | null {
  if (!linkSpend || !parentName) return null
  return `/early-signals/spend?supplier=${encodeURIComponent(parentName)}`
}

function tableAnswer(table: DbTableResult, content: string): SpendAnswer {
  return { kind: 'table', content, table }
}

export async function answerSpendPortfolio(
  companyId: string | null,
  linkSpend: boolean
): Promise<SpendAnswer> {
  if (!companyId) return { kind: 'text', content: NO_COMPANY }
  const dash = await fetchSpendDashboard(companyId, { year: null, bu: '', rollup: '' })
  if (!dash.hasData) return { kind: 'text', content: NO_CUBE }
  const rows = dash.topSuppliers.map((s) => [
    s.parentName,
    formatSpend(s.spend),
    formatRatio(s.share),
    s.band ? s.band.toUpperCase() : '—',
    s.priorityLine || '',
  ])
  const table: DbTableResult = {
    title: 'Spend by supplier',
    headers: ['Supplier', 'Spend', 'Share', 'Risk', 'Priority'],
    rows,
    sourceLabel: 'company spend cube',
    rowHrefs: dash.topSuppliers.map((s) => spendHref(s.parentName, linkSpend)),
  }
  const focus = dash.insights[0]?.text
  const lead = `Invoice spend ${formatSpend(dash.totalSpend)}, year over year ${formatYoy(dash.yoy)}, spend on high risk ${formatSpend(dash.highRiskSpend)}.`
  return tableAnswer(table, focus ? `${lead} ${focus}` : lead)
}

export async function answerSpendSupplier(
  companyId: string | null,
  parentName: string,
  linkSpend: boolean
): Promise<SpendAnswer> {
  if (!companyId) return { kind: 'text', content: NO_COMPANY }
  const row = await fetchSpendSupplier(companyId, parentName, { year: null, bu: '', rollup: '' })
  if (!row.hasData) {
    if (row.reason === 'no_cube') return { kind: 'text', content: NO_CUBE }
    return {
      kind: 'text',
      content: `No spend lines for “${parentName}” on this company’s cube.`,
    }
  }
  const name = row.parentName || parentName
  const table: DbTableResult = {
    title: `Spend synthesis — ${name}`,
    headers: ['Measure', 'Value'],
    rows: [
      ['Invoice spend', formatSpend(row.spend)],
      ['Share of company spend', formatRatio(row.share)],
      ['Year over year', formatYoy(row.yoy)],
      ['Categories', String(row.categoryCount ?? 0)],
      ['PO utilization', formatRatio(row.poUtilization)],
      ['Consultant share', formatRatio(row.consultantShare)],
      ['Risk band', row.linked ? (row.band ? row.band.toUpperCase() : 'Unscored') : 'Not linked'],
      ['Score', row.linked && row.score != null ? row.score.toFixed(1) : '—'],
      ['Single source', row.singleSource == null ? '—' : row.singleSource ? 'Yes' : 'No'],
      ['Open mitigations', String(row.openMitigations ?? 0)],
      ['Top risk', row.topRiskLine || '—'],
      ['Priority', row.priorityLine || '—'],
    ],
    sourceLabel: 'company spend cube',
    rowHrefs: [spendHref(name, linkSpend)],
  }
  const linkNote = row.linked
    ? `Risk score ${row.score != null ? row.score.toFixed(1) : '—'} is the shared supplier record.`
    : 'This parent name is not linked to an Early Signals supplier, so only spend is shown.'
  return tableAnswer(table, `${name}: ${formatSpend(row.spend)} (${formatRatio(row.share)} of company spend). ${linkNote}`)
}

export async function answerSpendCategory(
  companyId: string | null,
  categoryName: string,
  linkSpend: boolean
): Promise<SpendAnswer> {
  if (!companyId) return { kind: 'text', content: NO_COMPANY }
  const row = await fetchSpendCategory(companyId, categoryName, { year: null, bu: '' })
  if (!row.hasData) {
    if (row.reason === 'no_cube') return { kind: 'text', content: NO_CUBE }
    return {
      kind: 'text',
      content: `No spend category matching “${categoryName}” on this company’s cube.`,
    }
  }
  const suppliers = row.suppliers || []
  const table: DbTableResult = {
    title: `Spend — ${row.category}`,
    headers: ['Supplier', 'Spend', 'Share of category', 'Risk'],
    rows: suppliers.map((s) => [
      s.parentName,
      formatSpend(s.spend),
      formatRatio(s.share),
      s.band ? s.band.toUpperCase() : '—',
    ]),
    sourceLabel: 'company spend cube',
    rowHrefs: suppliers.map((s) => spendHref(s.parentName, linkSpend)),
  }
  return tableAnswer(
    table,
    `${row.category}: ${formatSpend(row.spend)} (${formatRatio(row.share)} of company spend). Spend-weighted risk ${
      row.weightedRisk != null ? row.weightedRisk.toFixed(1) : '—'
    }. Top supplier share ${formatRatio(row.topSupplierShare)}.`
  )
}

/** Short facts for narrative asks. The model may phrase these rows and must not add others. */
export async function spendFactsSection(companyId: string | null, supplierName?: string | null): Promise<string[]> {
  if (!companyId) return ['## Spend', 'No company in context, so spend figures are omitted.']
  const dash = await fetchSpendDashboard(companyId, { year: null, bu: '', rollup: '' })
  if (!dash.hasData) return ['## Spend', NO_CUBE]
  const lines = [
    '## Spend (this company only)',
    `Invoice spend ${formatSpend(dash.totalSpend)}; year over year ${formatYoy(dash.yoy)}; high-risk spend ${formatSpend(dash.highRiskSpend)}; single-source spend ${formatSpend(dash.singleSourceSpend)}.`,
  ]
  for (const row of dash.topSuppliers.slice(0, 8)) {
    lines.push(
      `- ${row.parentName}: ${formatSpend(row.spend)} (${formatRatio(row.share)}); band ${row.band || 'unscored'}${
        row.priorityLine ? `; ${row.priorityLine}` : ''
      }`
    )
  }
  if (supplierName) {
    const one = await fetchSpendSupplier(companyId, supplierName, { year: null, bu: '', rollup: '' })
    if (one.hasData) {
      lines.push(
        `Named supplier spend: ${one.parentName} ${formatSpend(one.spend)} (${formatRatio(one.share)}), band ${
          one.band || 'unscored'
        }, score ${one.score != null ? one.score.toFixed(1) : '—'}${
          one.priorityLine ? `, ${one.priorityLine}` : ''
        }.`
      )
    } else {
      lines.push(`Named supplier “${supplierName}” has no lines on this company’s spend cube.`)
    }
  }
  lines.push('Do not invent spend amounts, shares, or suppliers beyond this section.')
  return lines
}
